import styled from "styled-components";

/**
 * Layout mirrors the Patria styleguide lander: a centred single column on the
 * bone page ground, with two oversized feather watermarks bleeding off each edge.
 *
 * Values are ported from the reference's Tailwind classes and expressed in px,
 * NOT rem. styles/03-elements/base-font-sizing.scss drops the root from 62.5%
 * to 50% below 992px, so every rem here would silently shrink 20% on mobile —
 * which pushed the inputs under 16px and made iOS Safari zoom on focus.
 * Breakpoints follow Tailwind's sm = 640px / lg = 1024px, min-width first.
 */
export const Wrapper = styled.section`
  position: relative;
  overflow: hidden;
  background: var(--brand-bone);
`;

export const Feather = styled.div`
  pointer-events: none;
  position: absolute;
  width: 420px;
  height: 420px;
  opacity: 0.05;
  color: var(--brand-ink);

  svg {
    width: 100%;
    height: 100%;
    display: block;
  }

  &.feather-left {
    left: -96px;
    top: 96px;
    transform: rotate(-12deg);
  }

  &.feather-right {
    right: -96px;
    top: -40px;
    transform: rotate(45deg);
  }

  @media screen and (min-width: 1024px) {
    width: 560px;
    height: 560px;
  }
`;

export const Inner = styled.div`
  position: relative;
  margin: 0 auto;
  max-width: 768px;
  padding: 24px 20px 64px;
  text-align: center;

  @media screen and (min-width: 640px) {
    padding: 40px 32px 64px;
  }

  @media screen and (min-width: 1024px) {
    padding-bottom: 96px;
  }
`;

export const Eyebrow = styled.div`
  margin: 0 auto 24px;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  border-radius: 999px;
  background: #fff;
  padding: 6px 12px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.14em;
  color: var(--brand-red);
  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
  box-shadow:
    0 1px 2px 0 rgba(0, 0, 0, 0.05),
    0 0 0 1px rgba(0, 0, 0, 0.05);

  .dot {
    width: 6px;
    height: 6px;
    border-radius: 999px;
    background: var(--brand-green);
    flex-shrink: 0;
  }
`;

export const Headline = styled.h1`
  margin: 0 auto;
  max-width: 768px;
  font-family: var(--brand-display);
  font-size: 46px;
  font-weight: 800;
  line-height: 0.98;
  letter-spacing: -0.025em;
  color: var(--brand-ink);

  .line {
    display: block;
  }

  .line + .line {
    margin-top: 8px;
  }

  /* "Instant" is set in italic brand red */
  .accent {
    font-style: italic;
    color: var(--brand-red);
  }

  /* the translucent red swipe behind "You" */
  .marked {
    position: relative;
    display: inline-block;

    &:before {
      content: "";
      position: absolute;
      left: -6px;
      right: -6px;
      top: 16%;
      bottom: 12%;
      border-radius: 2px;
      background: rgba(209, 31, 61, 0.15);
      z-index: 0;
    }

    span {
      position: relative;
      z-index: 1;
    }
  }

  @media screen and (min-width: 640px) {
    font-size: 60px;
  }

  @media screen and (min-width: 1024px) {
    font-size: 76px;
  }
`;

export const Subhead = styled.p`
  margin: 20px auto 0;
  max-width: 576px;
  font-size: 16px;
  line-height: 1.625;
  color: rgba(14, 17, 22, 0.7);

  strong {
    font-weight: 600;
    color: var(--brand-ink);
  }

  .underlined {
    position: relative;
    display: inline-block;
    font-weight: 600;
    color: var(--brand-ink);

    svg {
      position: absolute;
      left: 0;
      bottom: -8px;
      width: 100%;
      height: 10px;
      color: var(--brand-red);
    }
  }

  @media screen and (min-width: 640px) {
    font-size: 18px;
  }
`;

export const CardWrap = styled.div`
  position: relative;
  margin: 40px auto 0;
  max-width: 576px;
`;

/** The two rotated stickers straddling the top edge of the form card. */
export const Sticker = styled.span`
  pointer-events: none;
  position: absolute;
  bottom: 100%;
  margin-bottom: -14px;
  z-index: 2;
  display: none;
  align-items: center;
  gap: 6px;
  border-radius: 999px;
  padding: 8px 16px;
  font-size: 11px;
  font-weight: 800;
  line-height: 1;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #fff;
  box-shadow:
    0 4px 6px -1px rgba(0, 0, 0, 0.1),
    0 0 0 2px #fff;

  .dot {
    width: 6px;
    height: 6px;
    border-radius: 999px;
    background: var(--brand-green);
    flex-shrink: 0;
  }

  &.sticker-left {
    left: 24px;
    transform: rotate(-4deg);
    background: var(--brand-red);
  }

  &.sticker-right {
    right: 24px;
    transform: rotate(4deg);
    background: var(--brand-ink);
  }

  @media screen and (min-width: 640px) {
    display: inline-flex;
  }
`;

export const Card = styled.div`
  position: relative;
  border-radius: 28px;
  border: 1px solid rgba(0, 0, 0, 0.05);
  background: #fff;
  padding: 24px;
  text-align: left;
  box-shadow: 0 30px 60px -30px rgba(14, 17, 22, 0.35);

  .card-eyebrow {
    margin: 0 0 4px;
    font-size: 12px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.1em;
    color: var(--brand-red);
  }

  .card-heading {
    margin: 0;
    font-family: var(--brand-display);
    font-size: 24px;
    font-weight: 800;
    line-height: 1.25;
    color: var(--brand-ink);

    .accent {
      color: var(--brand-red);
    }
  }

  .card-note {
    margin: 8px 0 0;
    font-size: 14px;
    color: rgba(14, 17, 22, 0.6);
  }

  .card-form {
    margin-top: 24px;
  }

  .mail-note {
    margin: 4px 0 0;
    text-align: center;
    font-size: 12px;
    color: rgba(14, 17, 22, 0.5);

    a {
      display: inline-block;
      font-weight: 600;
      color: var(--brand-red);
      text-decoration: none;
      text-underline-offset: 4px;
      /* widen the tap target without moving the caption */
      padding: 6px 4px;
      margin: -6px -4px;

      &:hover {
        text-decoration: underline;
      }
    }
  }

  @media screen and (min-width: 640px) {
    padding: 32px;

    .card-heading {
      font-size: 30px;
    }
  }
`;

export const Stats = styled.div`
  margin: 32px auto 0;
  display: grid;
  max-width: 576px;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
  text-align: center;

  .stat {
    border-radius: 16px;
    border: 1px solid rgba(0, 0, 0, 0.05);
    background: #fff;
    padding: 12px;
  }

  .stat-label {
    display: block;
    font-size: 10px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.025em;
    color: rgba(14, 17, 22, 0.5);
  }

  .stat-value {
    display: block;
    margin-top: 4px;
    font-size: 12px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.025em;
    color: var(--brand-red);
  }

  @media screen and (min-width: 640px) {
    .stat-label,
    .stat-value {
      font-size: 14px;
    }
  }
`;
